/**
 * `ActionButtons`
 * @param {IPlaylist} playlist optionals components.
 * @returns {JSX.Element} Component.
 */

import { MouseEvent, useMemo } from 'react';
import Tooltip from 'components/tooltip';
import ButtonIcon from 'components/buttonIcon';
import { ADD_USERS_LABEL } from 'constants/playlists';

import { ButtonsGapWrapper, IconButtonsContainer } from './styles';
import { IPlaylist } from 'contexts/playlists/types';
import { B2BActionProps, SonarActionProps } from './types';

const withStopPropagation =
  (callback: () => void) =>
  (event: MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    callback();
  };

const SonarRecommendeActionButtons = ({
  onAssignUser,
  onUnassignUser,
  onClonePlaylist,
  playlist,
  hideClone,
  alreadyAssigned,
}: SonarActionProps): JSX.Element => {
  const fullUserName = useMemo(() => {
    const cf = playlist?.created_for;
    const first = cf?.first_name ?? '';
    const last = cf?.last_name ?? '';
    return `${first} ${last}`.trim();
  }, [playlist]);

  return (
    <ButtonsGapWrapper hasPrimary={false}>
      <IconButtonsContainer>
        {!hideClone && (
          <Tooltip placement="top" title="Duplicate playlist">
            <ButtonIcon
              $secondary
              size="small"
              icon="Duplicate"
              onClick={withStopPropagation(() => onClonePlaylist(playlist))}
            />
          </Tooltip>
        )}
        {alreadyAssigned ? (
          <Tooltip placement="top" title={`Unassign ${fullUserName}`}>
            <ButtonIcon
              $secondary
              size="small"
              icon="UserMinus"
              onClick={withStopPropagation(() => onUnassignUser(playlist))}
            />
          </Tooltip>
        ) : (
          <Tooltip
            placement="top"
            overlayStyle={{
              width: '208px',
            }}
            title={`This playlist can only be assigned to ${fullUserName}. Click to assign.`}
          >
            <ButtonIcon
              $secondary
              size="small"
              icon="UserPlus"
              onClick={withStopPropagation(() => onAssignUser(playlist))}
            />
          </Tooltip>
        )}
      </IconButtonsContainer>
    </ButtonsGapWrapper>
  );
};

const B2BActionButtons = ({
  playlist,
  onClonePlaylist,
  onEditPlaylist,
  onAddUsers,
  onDeletePlaylist,
  disableAddUsers,
  hideAddUsers,
  addUsersTooltip,
  addUsersLabel,
}: B2BActionProps): JSX.Element => (
  <ButtonsGapWrapper hasPrimary={false}>
    <IconButtonsContainer>
      <Tooltip placement="top" title="Duplicate playlist">
        <ButtonIcon
          $secondary
          size="small"
          icon="Duplicate"
          onClick={withStopPropagation(() => onClonePlaylist(playlist))}
        />
      </Tooltip>
      <Tooltip placement="top" title="Edit playlist">
        <ButtonIcon
          $secondary
          size="small"
          icon="Edit"
          onClick={withStopPropagation(() => onEditPlaylist(playlist))}
        />
      </Tooltip>
      {!hideAddUsers && (
        <Tooltip
          placement="top"
          overlayInnerStyle={{ whiteSpace: 'pre-line', textAlign: 'center' }}
          title={
            disableAddUsers
              ? addUsersTooltip ?? addUsersLabel ?? ADD_USERS_LABEL
              : addUsersLabel ?? ADD_USERS_LABEL
          }
        >
          <span style={{ display: 'inline-flex' }}>
            <ButtonIcon
              $secondary
              size="small"
              icon="UserPlus"
              disabled={disableAddUsers}
              onClick={withStopPropagation(() => onAddUsers(playlist))}
            />
          </span>
        </Tooltip>
      )}
      <Tooltip placement="top" title="Delete playlist">
        <ButtonIcon
          $secondary
          size="small"
          icon="Trash"
          onClick={withStopPropagation(() => onDeletePlaylist(playlist))}
        />
      </Tooltip>
    </IconButtonsContainer>
  </ButtonsGapWrapper>
);

type Props = {
  playlist: IPlaylist;
  onAddUsers: (playlist: IPlaylist) => void;
  onDeletePlaylist: (playlist: IPlaylist) => void;
  onEditPlaylist: (playlist: IPlaylist) => void;
  onClonePlaylist: (playlist: IPlaylist) => void;
  onAssignUser: (playlist: IPlaylist) => void;
  onUnassignUser: (playlist: IPlaylist) => void;
  isSonar: boolean;
  hideClone?: boolean;
  alreadyAssigned: boolean;
  disableAddUsers?: boolean;
  hideAddUsers?: boolean;
  addUsersTooltip?: string;
  addUsersLabel?: string;
};

const ActionButtons = ({
  playlist,
  onAddUsers,
  onDeletePlaylist,
  onEditPlaylist,
  onClonePlaylist,
  onAssignUser,
  onUnassignUser,
  isSonar,
  hideClone,
  alreadyAssigned,
  disableAddUsers,
  hideAddUsers,
  addUsersTooltip,
  addUsersLabel,
}: Props): JSX.Element => {
  const commonProps = {
    playlist,
    onClonePlaylist,
  };

  const sonarRecommendedProps = {
    ...commonProps,
    onAssignUser,
    onUnassignUser,
    hideClone,
    alreadyAssigned,
  };

  const b2bProps = {
    ...commonProps,
    onAddUsers,
    onDeletePlaylist,
    onEditPlaylist,
    disableAddUsers,
    hideAddUsers,
    addUsersTooltip,
    addUsersLabel,
  };

  if (isSonar) return <SonarRecommendeActionButtons {...sonarRecommendedProps} />;
  return <B2BActionButtons {...b2bProps} />;
};

export default ActionButtons;
